import { BaseError } from './base-error';
import { ErrorCategory, ErrorCode, ErrorSeverity } from './types';
import type { ErrorContext } from './types';

export interface NetworkErrorOptions {
  code: ErrorCode;
  message: string;
  context?: ErrorContext;
  cause?: Error;
  severity?: ErrorSeverity;
  statusCode?: number;
}

/**
 * Network and HTTP related errors
 */
export class NetworkError extends BaseError {
  public readonly statusCode?: number;

  constructor(options: NetworkErrorOptions) {
    super({
      ...options,
      category: ErrorCategory.Network,
      severity: options.severity || ErrorSeverity.Error,
      context: {
        ...options.context,
        statusCode: options.statusCode || options.context?.statusCode,
      },
    });

    Object.setPrototypeOf(this, NetworkError.prototype);

    this.statusCode = options.statusCode;
  }

  /**
   * Create a request failed error
   */
  static requestFailed(
    url: string,
    statusCode?: number,
    context?: ErrorContext,
    cause?: Error
  ): NetworkError {
    return new NetworkError({
      code: ErrorCode.NETWORK_REQUEST_FAILED,
      message: `Request to ${url} failed${statusCode ? ` with status ${statusCode}` : ''}`,
      statusCode,
      context: {
        ...context,
        url,
      },
      cause,
    });
  }

  /**
   * Create a timeout error
   */
  static timeout(
    url: string,
    timeoutMs: number,
    context?: ErrorContext,
    cause?: Error
  ): NetworkError {
    return new NetworkError({
      code: ErrorCode.NETWORK_TIMEOUT,
      message: `Request to ${url} timed out after ${timeoutMs}ms`,
      context: {
        ...context,
        url,
        metadata: {
          ...context?.metadata,
          timeoutMs,
        },
      },
      cause,
    });
  }

  /**
   * Create a connection refused error
   */
  static connectionRefused(
    url: string,
    context?: ErrorContext,
    cause?: Error
  ): NetworkError {
    return new NetworkError({
      code: ErrorCode.NETWORK_CONNECTION_REFUSED,
      message: `Connection refused to ${url}`,
      context: {
        ...context,
        url,
      },
      cause,
    });
  }

  /**
   * Create a DNS error
   */
  static dnsError(
    hostname: string,
    context?: ErrorContext,
    cause?: Error
  ): NetworkError {
    return new NetworkError({
      code: ErrorCode.NETWORK_DNS_ERROR,
      message: `DNS resolution failed for ${hostname}`,
      context: {
        ...context,
        metadata: {
          ...context?.metadata,
          hostname,
        },
      },
      cause,
    });
  }

  /**
   * Create a rate limited error
   */
  static rateLimited(
    retryAfter?: number,
    context?: ErrorContext,
    cause?: Error
  ): NetworkError {
    const message = retryAfter
      ? `Rate limited. Retry after ${retryAfter} seconds`
      : 'Rate limited';

    return new NetworkError({
      code: ErrorCode.NETWORK_RATE_LIMITED,
      message,
      statusCode: 429,
      context: {
        ...context,
        metadata: {
          ...context?.metadata,
          retryAfter,
        },
      },
      cause,
      severity: ErrorSeverity.Warning,
    });
  }

  /**
   * Override toJSON to include network-specific fields
   */
  override toJSON(): Record<string, unknown> {
    const base = super.toJSON();
    return {
      ...base,
      statusCode: this.statusCode,
    };
  }

  /**
   * Create network error from fetch response
   */
  static async fromResponse(
    response: Response,
    context?: ErrorContext
  ): Promise<NetworkError> {
    let message = `HTTP ${response.status} ${response.statusText}`;

    try {
      // Try to get error details from response body
      const body = await response.text();
      if (body) {
        try {
          const parsed = JSON.parse(body);
          if (parsed.error && typeof parsed.error === 'string') {
            message = parsed.error;
          } else if (parsed.message && typeof parsed.message === 'string') {
            message = parsed.message;
          }
        } catch {
          // Body is not JSON, use as is if it's reasonably short
          if (body.length < 200) {
            message = body;
          }
        }
      }
    } catch {
      // Ignore errors getting response body
    }

    return new NetworkError({
      code: ErrorCode.NETWORK_REQUEST_FAILED,
      message,
      statusCode: response.status,
      context: {
        ...context,
        url: response.url,
        statusCode: response.status,
      },
    });
  }
}
